/**
 * `GET /entities/tree` — eine Ebene des Objektbaums, für `components/
 * EntityPicker.tsx` (Spec 05a `access-invite-entity`, Spec 06a
 * `task-cost-center`).
 *
 * Liegt hier und nicht in einem `features/*\/api.ts`: `EntityPicker` ist
 * geteilte Oberfläche in `components/` (`apps/web/CLAUDE.md`, „Gemeinsames
 * nach components/ oder lib/"), und `components/` darf `features/*` nicht
 * importieren (eslint-boundaries) — die Abfrage dahinter muss deshalb
 * ebenfalls dorthin, sonst bliebe der Baum an eine der beiden Domänen
 * gebunden, die ihn benutzen. `features/objects/api.ts::useEntitiesTree`
 * bleibt bewusst unverändert: dieselbe Abfrage ein drittes Mal, vor 006a
 * entstanden und nicht Teil dieses Befunds (`reports/14-simplifier.md`,
 * `docs/work/006a-aufgaben-2a164c/findings.md`, „Tor 2").
 *
 * Seit Spec 09 zusätzlich `useEntityTreeSearch`: der flache Suchmodus
 * (`?q=&kind=`) des Objektpickers (`document-subject-picker`). Ein eigener
 * Hook statt eines dritten Parameters an `useEntityTreeLevel` — die beiden
 * Betriebsarten liefern verschiedene Seitenformen (Ebene nach `parent_id`
 * gegen flache Trefferliste) und `useEntityTreeLevel` bleibt dadurch für
 * `access-invite-entity`/`task-cost-center` byteidentisch (Regel 9, opt-in
 * laut Spec 09 „Der Picker bekommt einen Suchmodus").
 *
 * Seit LUK-48 zusätzlich `useEntityHead`/`expandedAncestorIds`: Öffnet der
 * Baum mit gesetztem `value`, sollen seine Vorfahren bereits aufgeklappt
 * sein. `GET /entities/{id}` liefert den Pfad dorthin (`ObjectHead.path`) —
 * dieselbe Abfrage wie `features/objects/api.ts::useEntityHead`, absichtlich
 * ein zweites Mal hier: `components/` darf `features/*` nicht importieren
 * (eslint-boundaries), und `EntityPicker` liegt in `components/`. Beide
 * teilen sich trotzdem den Query-Key (`keys.entities.detail`), also den
 * Cache — wer das Objekt zuvor schon im Objektbildschirm offen hatte, löst
 * hier keinen zweiten Netzwerkruf aus.
 */

import { useQuery } from '@tanstack/react-query'

import type { components } from '@/lib/api-types'
import { request } from '@/lib/api'
import { keys } from '@/lib/query-keys'

export type EntityTreeNode = components['schemas']['TreeNode']

interface EntityTreePage {
  readonly items: EntityTreeNode[]
  readonly next_cursor: string | null
}

interface UseEntityTreeLevelOptions {
  readonly enabled?: boolean
}

/** Obergrenze der Seitengröße (`shared/pagination.py::MAX_PAGE_SIZE`): weniger Runden je Ebene.
 * Die erste Anfrage bleibt, wie sie war (Vorgabe 50); nur die Folgeseiten holen 200. */
const TREE_PAGE_SIZE = 200

/**
 * Lädt eine ganze Ebene: folgt `next_cursor`, bis er `null` ist, und fügt die
 * Seiten zu einer zusammen. Eine einzelne Seite (Vorgabe 50) machte das 51.
 * Objekt einer Ebene im Picker unerreichbar — es gibt dort kein „Mehr laden".
 * ponytail: lädt die ganze Ebene; bei Tausenden Objekten je Ebene eine virtuelle
 * Liste oder „Mehr laden" im Picker.
 */
export async function fetchEntityTreeLevel(parentId: string | undefined, signal: AbortSignal): Promise<EntityTreePage> {
  const items: EntityTreeNode[] = []
  let cursor: string | null = null
  do {
    const params = new URLSearchParams()
    if (parentId !== undefined) params.set('parent_id', parentId)
    if (cursor !== null) {
      params.set('limit', String(TREE_PAGE_SIZE))
      params.set('cursor', cursor)
    }
    const query = params.toString()
    const page: EntityTreePage = await request<EntityTreePage>(`/entities/tree${query === '' ? '' : `?${query}`}`, { signal })
    items.push(...page.items)
    cursor = page.next_cursor
  } while (cursor !== null)
  return { items, next_cursor: null }
}

/** Eine Ebene des Objektbaums. `parentId` fehlt für die Wurzeln. */
export function useEntityTreeLevel(parentId: string | undefined, options: UseEntityTreeLevelOptions = {}) {
  return useQuery({
    queryKey: keys.entities.list({ parentId: parentId ?? null }),
    queryFn: ({ signal }) => fetchEntityTreeLevel(parentId, signal),
    enabled: options.enabled ?? true,
  })
}

interface UseEntityTreeSearchOptions {
  readonly enabled?: boolean
}

/**
 * Der flache Suchmodus von `GET /entities/tree` (Spec 09, „Der Picker
 * bekommt einen Suchmodus"): `q` ab zwei Zeichen ersetzt die hierarchische
 * Betriebsart durch eine flache, nach `label` sortierte Trefferliste mit
 * gefülltem `path_label`. `kind` grenzt die Arten ein — für Dokumente die
 * fünf aus `ALLOWED_PARENT_KINDS["document"]` (`DocumentSubjectField.tsx`).
 * Serverseitig 422 unter zwei Zeichen — der Aufrufer schaltet deshalb selbst
 * über `enabled` ab, ruft also nie mit einem zu kurzen `q`.
 */
export function useEntityTreeSearch(q: string, kind: readonly string[], options: UseEntityTreeSearchOptions = {}) {
  return useQuery({
    queryKey: keys.entities.list({ q, kind }),
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ q })
      for (const value of kind) params.append('kind', value)
      return request<EntityTreePage>(`/entities/tree?${params.toString()}`, { signal })
    },
    enabled: options.enabled ?? true,
  })
}

export type EntityHead = components['schemas']['ObjectHead']

interface UseEntityHeadOptions {
  readonly enabled?: boolean
}

/** Der Kopf eines einzelnen Objekts — hier nur für seinen Pfad
 * (`expandedAncestorIds`) und `archived_at` gebraucht (LUK-48). `entityId`
 * fehlt beim Anlegen/leeren Feld — dann bleibt die Abfrage aus, statt gegen
 * `/entities/undefined` zu laufen. */
export function useEntityHead(entityId: string | undefined, options: UseEntityHeadOptions = {}) {
  return useQuery({
    queryKey: keys.entities.detail(entityId ?? ''),
    queryFn: ({ signal }) => {
      // `enabled` unten hält diesen Aufruf ab, solange `entityId` fehlt —
      // hier zusätzlich geprüft, damit kein Aufruf gegen `/entities/undefined`
      // entsteht, sollte die Abfrage doch einmal ohne `enabled` laufen.
      if (entityId === undefined) throw new Error('useEntityHead ohne entityId aufgerufen')
      return request<EntityHead>(`/entities/${entityId}`, { signal })
    },
    enabled: (options.enabled ?? true) && entityId !== undefined,
    // Kein Fehlerzustand in der Oberfläche — eine Vorauswahl ohne Zugriff,
    // archiviert oder gelöscht (403/404) soll den Baum einfach zugeklappt
    // lassen (Ticket LUK-48), nicht dreimal mit wachsender Pause nachfragen.
    retry: false,
  })
}

/** Welche Knoten-Ids der Baum aufklappen soll, um zu `head` zu führen
 * (LUK-48, „Objektauswahl als aufklappbarer Baum mit Vorauswahl"): die
 * Vorfahren aus `head.path` (Wurzel bis Elternobjekt, `head` selbst zählt
 * nicht dazu — seine Zeile liegt schon im letzten aufgeklappten Elternknoten).
 * Leere Menge, wenn `head` fehlt oder archiviert ist — der Baum bleibt dann
 * zugeklappt, ohne dass ein Fehler entsteht (Ticket: „Nicht sichtbare/
 * archivierte/404 Vorauswahl → Baum zugeklappt ohne Fehler"). */
export function expandedAncestorIds(head: EntityHead | undefined): ReadonlySet<string> {
  if (head?.archived_at !== null) return new Set()
  return new Set(head.path.map((entry) => entry.id))
}
